/**
 * LabUploadModal.tsx
 *
 * V5 parity for legacy notes-document-new.php?labupload=1.
 *
 * Two-step selection: a hardcoded Category picker (Lab Results / Lab Orders)
 * gates which notes_types entries appear in the Type dropdown. Save flow:
 *   1. createNote (carries typeId + visibleToPatient)
 *   2. uploadAttachment per file
 *   3. processLab (only for Lab Results) → kicks off legacy textract +
 *      biomarker extraction via the v5→v4 HMAC proxy.
 *
 * When `forceFileType` is set (Imaging/Genetics tab upload) the Category picker
 * is hidden and locked to Lab Results (isLab=1, so the note lands in the labs
 * bucket), each attachment is pre-categorized via `fileType`, and processLab is
 * skipped — the classifier never runs, so the explicit category sticks.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload, X, Paperclip, Loader2 } from 'lucide-react';
import { toast } from '@/store/toastStore';
import * as notesSvc from '@/v5/services/notes.service';
import { Button } from '@/components/ui/Button';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

const CATEGORY_OPTIONS: ComboboxOption<string>[] = [
  { value: '1', label: 'Lab Results' },
  { value: '2', label: 'Lab Orders' },
];

/**
 * Every field in this form is blocked on by `handleSave`, and none of them said so —
 * SCRUM-1419 reported that as "it is not clear whether it must be filled before
 * saving". `aria-hidden` because the asterisk is decoration; the field's own error
 * toast is what actually communicates the requirement.
 */
function RequiredMark() {
  return (
    <span className="text-red-500" aria-hidden="true">
      {' *'}
    </span>
  );
}

interface LabUploadModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
  /**
   * When set, pre-categorize the upload (Imaging/Genetics tab). Hides the
   * Category picker, locks it to Lab Results (isLab=1), stamps file_type on each
   * attachment, and skips the classifier kickoff.
   */
  forceFileType?: 'IMAGING' | 'GENETICS';
}

export function LabUploadModal({ patientId, open, onClose, onUploaded, forceFileType }: LabUploadModalProps) {
  const [allNoteTypes, setAllNoteTypes] = useState<notesSvc.NoteCategory[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<string>('0');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  // Files whose printed patient name does not agree with this chart.
  const [subjectWarning, setSubjectWarning] = useState<{ file: string; found: string }[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTypes(true);
    notesSvc
      .listNoteCategories()
      .then((all) => {
        if (cancelled) return;
        setAllNoteTypes(all.filter((c) => (c.isLab === 1 || c.isLab === 2) && c.active));
      })
      .catch((err) => {
        console.error('[LabUploadModal] listNoteCategories failed:', err);
        toast.error('Failed to load lab types');
      })
      .finally(() => {
        if (!cancelled) setLoadingTypes(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (open) return;
    setCategory(null);
    setTypeId('0');
    setTitle('');
    setFiles([]);
    setDragOver(false);
    setSaving(false);
  }, [open]);

  // Pre-categorized upload (Imaging/Genetics): lock the category to Lab Results
  // (isLab=1) so the note lands in the labs bucket and the Type dropdown fills.
  useEffect(() => {
    if (open && forceFileType) setCategory('1');
  }, [open, forceFileType]);

  // Reset type selection when category changes
  useEffect(() => {
    setTypeId('0');
  }, [category]);

  const typeOptions = useMemo<ComboboxOption<string>[]>(() => {
    if (!category) return [];
    const isLabVal = parseInt(category, 10);
    return allNoteTypes
      .filter((c) => c.isLab === isLabVal)
      .map((c) => ({ value: String(c.noteTypeID), label: c.name }));
  }, [allNoteTypes, category]);

  function addFiles(newFiles: FileList | File[] | null) {
    if (!newFiles) return;
    const arr = Array.from(newFiles);
    if (arr.length === 0) return;
    setFiles((prev) => [...prev, ...arr]);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    if (saving) return;

    if (!category) {
      toast.error('Please select a category.');
      return;
    }
    if (!typeId || typeId === '0') {
      toast.error('Please select a type.');
      return;
    }
    if (!title.trim()) {
      toast.error('Please enter a subject.');
      return;
    }
    if (files.length === 0) {
      toast.error('Please add at least one file.');
      return;
    }

    // SCRUM-1806: does the file name the patient it is being filed under?
    // Runs BEFORE anything is created, so a document that names someone else
    // can still be cancelled — once attached it is already in the wrong chart.
    //
    // Only a positive mismatch stops to ask. Unreadable scans, formats the
    // reader cannot open, and any failure of the check itself all fall through
    // silently: this must never be the reason a clinic cannot file a lab.
    const mismatches: { file: string; found: string }[] = [];
    for (const f of files) {
      try {
        const check = await notesSvc.verifyLabSubject(patientId, f);
        if (check.verdict === 'none' && check.documentName) {
          mismatches.push({ file: f.name, found: check.documentName });
        }
      } catch {
        // Advisory only — never block the upload on the checker.
      }
    }
    if (mismatches.length > 0) {
      setSubjectWarning(mismatches);
      return;
    }

    await doUpload();
  }

  async function doUpload() {
    setSubjectWarning(null);
    setSaving(true);
    try {
      const note = await notesSvc.createNote(patientId, {
        title: title.trim(),
        typeId: parseInt(typeId, 10),
        visibleToPatient: true,
      });

      await Promise.all(
        files.map((f) =>
          notesSvc.uploadAttachment(
            patientId,
            note.noteId,
            f,
            forceFileType ? { fileType: forceFileType } : undefined,
          ),
        ),
      );

      // Skip the classifier for pre-categorized uploads — the explicit file_type
      // is authoritative, and running processLab would re-run the Bedrock chain.
      if (category === '1' && !forceFileType) {
        try {
          await notesSvc.processLab(patientId, note.noteId);
        } catch (err) {
          console.error('[LabUploadModal] processLab kickoff failed:', err);
        }
      }

      toast.success(
        forceFileType
          ? 'Document uploaded.'
          : 'Uploaded labs are processing and will be available soon.',
      );
      onUploaded();
    } catch (err) {
      console.error('[LabUploadModal] save failed:', err);
      toast.error(err instanceof notesSvc.AttachmentUploadError ? err.message : 'Failed to upload lab.');
    } finally {
      setSaving(false);
    }
  }

  /*
    This modal is hand-rolled rather than the shared `Modal`, so it carried no scroll
    lock at all — part of SCRUM-1423's "the same behaviour happens on every modal in the
    app". Above the early return, called unconditionally.
  */
  useBodyScrollLock(open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 pb-4 max-md:p-0">
      {/* SCRUM-1806. Shown only on a positive mismatch — a name was read and it
          is not this patient's. Names both sides so the person filing can tell
          at a glance whether it is a maiden name they recognise or somebody
          else's results entirely. Filing anyway is one click, because the
          reader is not always right and staff are.

          z-20 deliberately: the modal panel below is z-10, and this sat at
          z-10 too — same layer, later sibling wins, so the question rendered
          BEHIND the form it was asking about and only its buttons showed. It
          has to outrank the panel, not tie with it. */}
      {subjectWarning && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900">
              This file may belong to someone else
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {subjectWarning.length === 1
                ? 'The name on this file does not match this patient.'
                : `The names on ${subjectWarning.length} files do not match this patient.`}
            </p>
            <ul className="mt-3 space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              {subjectWarning.map((m) => (
                <li key={m.file} className="text-amber-900">
                  <span className="font-medium">{m.file}</span> names{' '}
                  <span className="font-medium">{m.found}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              Filing it here puts these results in this patient&apos;s chart.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => setSubjectWarning(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700"
                onClick={() => void doUpload()}
              >
                Upload anyway
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={saving ? undefined : onClose}
      />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden max-md:h-full max-md:max-h-none max-md:rounded-none">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-3 bg-slate-50">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Upload className="h-4 w-4" />
            {forceFileType === 'IMAGING'
              ? 'Upload Scans/Other'
              : forceFileType === 'GENETICS'
                ? 'Upload Genetics'
                : 'Upload Lab'}
          </h2>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} loading={saving}>
              Save
            </Button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!forceFileType && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Category<RequiredMark /></label>
              <SearchableCombobox<string>
                mode="single"
                options={CATEGORY_OPTIONS}
                value={category}
                onChange={(v) => setCategory(v)}
                placeholder="Select one..."
                emptyMessage="No categories available"
                disabled={saving}
                searchByLabelOnly
              />
            </div>
          )}

          {category && (
            <div>
              {/*
                SCRUM-1419 asked for this field to be removed. It is not an orphan:
                `handleSave` blocks on it, `createNote` receives it as `typeId`, and the
                Category → Type gate is what this modal's docblock describes as its core
                design. Removing it would need a server-side default.

                The reported confusion — "it is empty by default and it is not clear
                whether it is required" — is real though, and it applies to all four
                fields in this form: every one is validated on save and none carried a
                marker. Marking them is the fix; marking only this one would read as
                arbitrary.
              */}
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Type<RequiredMark />
              </label>
              <SearchableCombobox<string>
                mode="single"
                options={typeOptions}
                value={typeId === '0' ? null : typeId}
                onChange={(v) => setTypeId(v ?? '0')}
                placeholder={loadingTypes ? 'Loading…' : 'Select one...'}
                emptyMessage="No types configured for this category"
                disabled={loadingTypes || saving}
                searchByLabelOnly
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Subject<RequiredMark /></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={saving}
              placeholder="e.g. Quest CBC results, Vibrant Wellness panel…"
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Add Documents<RequiredMark /></label>
            <div
              className={
                'rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors ' +
                (dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-slate-300 bg-slate-50 hover:bg-slate-100')
              }
              onDragOver={(e) => {
                e.preventDefault();
                if (!saving) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (saving) return;
                addFiles(e.dataTransfer.files);
              }}
            >
              <p className="text-xs text-slate-500 mb-2">Drop files here</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  addFiles(e.target.files);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                disabled={saving}
                className="hidden"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
              >
                Add Document
              </Button>
            </div>

            {files.length > 0 && (
              <div className="mt-2 border rounded-md divide-y divide-slate-100 bg-white">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between px-3 py-1.5 text-xs"
                  >
                    <span className="flex items-center gap-1.5 truncate text-slate-700">
                      <Paperclip className="h-3 w-3 text-slate-400" />
                      <span className="truncate">{f.name}</span>
                      <span className="text-slate-400">({Math.round(f.size / 1024)} KB)</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      disabled={saving}
                      className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-50"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {saving && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Uploading…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
